import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * SectorChip — color-coded badge for `client_type` on the clients table
 * (T-0108 / E01-F01).
 *
 * Sector vocabulary mirrors the `clients_client_type_check` Postgres
 * constraint from `db/prisma/migrations/20260528000007_clients_and_team_members
 * /migration.sql`:
 *
 *     state_agency | county | city | other
 *
 * The ticket asks for "4 visually-distinct tones from the existing palette
 * — DO NOT introduce new hex codes." We derive distinct visual treatments
 * by composing existing posture and accent tint pairs from
 * `design_handoff_caliber_v2/README.md §5` (canonical tokens):
 *
 *   - state_agency → AI surface (lavender) — institutional, brand-adjacent
 *   - county       → posture-healthy-tint + posture-healthy ink (green) —
 *                    the broadest "ordinary local government" category
 *   - city         → accent-amber-50 + accent-amber-700 (warm amber) —
 *                    distinct from posture amber to avoid status-coding it
 *   - other        → ink-100 + ink-700 (neutral grey) — "unassessed" tone
 *                    for the catch-all
 *
 * The mapping uses the posture/AI/accent palettes only — no raw hex. The
 * tones are deliberately *categorical*, not posture-semantic, so a
 * reader doesn't mistake "city green" for "healthy."
 *
 * Geometry matches the load-bearing chip base in
 * `apps/web/src/components/ui/posture-chip.tsx` — 22px tall pill, 12px /
 * 500 / 18px line-height, whitespace-nowrap.
 */
export type ClientSector = 'state_agency' | 'county' | 'city' | 'other';

export interface SectorChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  sector: string;
}

const SECTOR_LABEL: Record<ClientSector, string> = {
  state_agency: 'State agency',
  county: 'County',
  city: 'City',
  other: 'Other',
};

const SECTOR_CLASSES: Record<ClientSector, string> = {
  // AI surface lavender — institutional / brand-adjacent.
  state_agency: 'bg-ai-surface text-ai-ink',
  // posture-healthy-tint + posture-healthy ink — categorical, not status.
  county: 'bg-posture-healthy-tint text-posture-healthy',
  // accent-amber-50 + accent-amber-700 — warm, distinct from posture amber.
  city: 'bg-accent-amber-50 text-accent-amber-700',
  // ink-100 + ink-700 — neutral catch-all (matches `.chip-neutral`).
  other: 'bg-ink-100 text-ink-700',
};

function normalizeSector(sector: string): ClientSector {
  if (sector === 'state_agency' || sector === 'county' || sector === 'city') {
    return sector;
  }
  return 'other';
}

export const SectorChip = React.forwardRef<HTMLSpanElement, SectorChipProps>(
  ({ sector, className, ...props }, ref) => {
    const normalized = normalizeSector(sector);
    return (
      <span
        ref={ref}
        role="status"
        aria-label={`Sector: ${SECTOR_LABEL[normalized]}`}
        className={cn(
          'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5',
          'text-meta font-medium leading-[18px]',
          SECTOR_CLASSES[normalized],
          className,
        )}
        {...props}
      >
        {SECTOR_LABEL[normalized]}
      </span>
    );
  },
);
SectorChip.displayName = 'SectorChip';

export { SECTOR_LABEL };
